import React, { useContext, useState } from 'react';
import {
  BdsButton,
  BdsTypo,
  BdsGrid,
  BdsInput,
  BdsStepper,
  BdsStep,
  BdsUpload,
  BdsList,
  BdsListItem,
} from 'blip-ds/dist/blip-ds-react';
import MtlsContext from '../../../../contexts/MtlsContext';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { localization } from './localization';
import { showToast } from '../../../../api/CommonApi';
import { RequestStatus } from '../../../../shared/Requests';

enum Step {
  upload = 0,
  info = 1,
  confirmation = 2,
  finish = 3,
}

const FILE_TYPE = 'application/x-pkcs12';
const MAX_FILE_SIZE = 10;

const validateUpload = (fileName: string) => {
  return fileName !== '';
};

const validateInfo = (description: string, hosts: [{ host: string; validUrl: boolean }]) => {
  return hosts.every((item) => item.validUrl && item.host !== '') && description !== '';
};

const validateConfirmation = (fileName: string, description: string, hosts: [{ host: string; validUrl: boolean }]) => {
  return validateUpload(fileName) && validateInfo(description, hosts);
};

const validateFile = (file: File, content) => {
  if (!file) {
    return showToast({
      type: 'danger',
      message: content.upload.error,
    });
  }
  if (![FILE_TYPE].includes(file.type)) {
    return showToast({
      type: 'danger',
      message: content.upload.typeError,
    });
  }

  if (file.size / (1024 * 1024) > MAX_FILE_SIZE) {
    return showToast({
      type: 'danger',
      message: content.upload.sizeError,
    });
  }
  return true;
};

const UploadCertificate: React.FC = () => {
  const { certificateInputs, setCertificateInputs } = useContext(MtlsContext);
  const content = useContentLocalizer(localization);

  const handleChangeUploadInput = (event) => {
    const file = event.detail.value;

    setCertificateInputs((prevInputs) => ({
      ...prevInputs,
      file: file[0],
      fileName: file[0]?.name,
    }));
  };

  const handlePasswordChange = (event) => {
    const { value } = event.target;
    setCertificateInputs((prevInputs) => ({
      ...prevInputs,
      password: value,
    }));
  };

  return (
    <BdsGrid direction="column" gap="2" height="100%" padding="none">
      <BdsUpload
        language={content.locale}
        id="certificate-upload"
        titleName={content.upload.title}
        subtitle={content.upload.subtitle}
        defaultValue={certificateInputs.fileName}
        dataAccept={['.pfx']}
        multiple={false}
        dtInputFiles="input-fileNames"
        dtLabelAddFile="label-add-fileName"
        dtButtonDelete="button-delete"
        onBdsUploadChange={(e) => handleChangeUploadInput(e)}
      ></BdsUpload>
      <BdsGrid xxs="6" padding="l-none">
        <BdsInput
          value={certificateInputs.password}
          type="password"
          label={content.upload.passwordLabel}
          placeholder={content.upload.passwordPlaceholder}
          onBdsChange={(e) => handlePasswordChange(e)}
        ></BdsInput>
      </BdsGrid>
    </BdsGrid>
  );
};

const InfoCertificate: React.FC = () => {
  const { certificateInputs, setCertificateInputs } = useContext(MtlsContext);

  const validHttpsUrl = (host) => {
    const hasDuplicatedUrl = certificateInputs?.hosts?.every((item) => item.host !== host);
    const hostRegex = /^https:\/\/[a-zA-Z0-9\-\.]+\.[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;
    return hasDuplicatedUrl && hostRegex.test(host);
  };

  const handleAddUrlInput = () => {
    setCertificateInputs((prevInputs) => ({
      ...prevInputs,
      hosts: [...certificateInputs.hosts, { host: '', validUrl: true }],
    }));
  };

  const handleChangeDescriptionInput = (event) => {
    const { value } = event.target;

    setCertificateInputs((prevInputs) => ({
      ...prevInputs,
      description: value,
    }));
  };

  const handleChangeUrlInput = (event, index) => {
    const { inputName, value } = event.target;
    const newArray = [...certificateInputs.hosts];
    newArray[index] = { ...newArray[index], [inputName]: value, validUrl: validHttpsUrl(value) };

    setCertificateInputs((prevInputs) => ({
      ...prevInputs,
      hosts: newArray,
    }));
  };

  const handleDeleteUrlInput = (index) => {
    const newArray = [...certificateInputs.hosts];
    newArray.splice(index, 1);
    setCertificateInputs((prevInputs) => ({
      ...prevInputs,
      hosts: newArray,
    }));
  };

  const content = useContentLocalizer(localization);
  return (
    <BdsGrid xxs="12" direction="column" gap="2" height="100%" padding="none">
      <BdsGrid xxs="12" flexWrap="wrap" padding="none" justifyContent="space-between">
        <BdsGrid xxs="12" padding="l-none" margin="b-2">
          <BdsInput
            value={certificateInputs.description}
            required={true}
            label={content.info.descriptionLabel}
            placeholder={content.info.descriptionPlaceholder}
            requiredErrorMessage={content.info.validCertificateDescriptionError}
            onBdsChange={(e) => handleChangeDescriptionInput(e)}
            maxlength={50}
            maxErrorMessage={content.info.descriptionMaxErrorMessage}
          ></BdsInput>
        </BdsGrid>
        {certificateInputs.hosts?.map((item, index) => (
          <BdsGrid xxs="12" padding="l-none" margin="b-2" key={index}>
            <BdsGrid xxs="10" padding="l-none">
              <BdsInput
                inputName="host"
                value={item.host}
                required={true}
                label={content.info.urlLabel}
                placeholder={content.info.urlPlaceholder}
                onBdsChange={(e) => handleChangeUrlInput(e, index)}
                danger={!item.validUrl}
                errorMessage={content.info.validUrlerror}
                maxlength={100}
                maxErrorMessage={content.info.urlMaxErrorMessage}
              ></BdsInput>
            </BdsGrid>
            <BdsGrid xxs="2" padding="l-none" margin="t-1">
              {certificateInputs.hosts.length > 1 && (
                <BdsGrid xxs="6">
                  <BdsButton variant="delete" onClick={() => handleDeleteUrlInput(index)}>
                    -
                  </BdsButton>
                </BdsGrid>
              )}
              {index === certificateInputs.hosts.length - 1 && (
                <BdsGrid xxs="6">
                  <BdsButton variant="primary" onClick={() => handleAddUrlInput()}>
                    +
                  </BdsButton>
                </BdsGrid>
              )}
            </BdsGrid>
          </BdsGrid>
        ))}
      </BdsGrid>
    </BdsGrid>
  );
};

const CertificateConfirmation: React.FC = () => {
  const content = useContentLocalizer(localization);
  const { certificateInputs } = useContext(MtlsContext);
  return (
    <BdsList>
      <BdsTypo variant="fs-20" bold="bold" margin={true}>
        {content.confirmation.subtitle}
      </BdsTypo>
      <BdsTypo variant="fs-16" bold="bold" margin={true}>
        {content.confirmation.file}
      </BdsTypo>
      <BdsListItem value="01" text={certificateInputs.fileName}></BdsListItem>
      <BdsTypo variant="fs-16" bold="bold" margin={true}>
        {content.confirmation.description}
      </BdsTypo>
      <BdsListItem value="02" text={certificateInputs.description}></BdsListItem>
      <BdsTypo variant="fs-16" bold="bold" margin={true}>
        {content.confirmation.url}
      </BdsTypo>
      {certificateInputs.hosts.map((item, index) => (
        <BdsListItem key={index} value={3 + index} text={item.host}></BdsListItem>
      ))}
    </BdsList>
  );
};

const SubmitCertificate: React.FC = () => {
  const [step, setStep] = useState(Step.upload as Step);
  const content = useContentLocalizer(localization);
  const {
    toggleSubmitModal,
    certificateInputs,
    setCertificateInputs,
    emptyCertificate,
    mtlsCertificateApi,
    tenantId,
    setCertificateUpdatedProps,
  } = useContext(MtlsContext);
  const [isLoading, setIsLoading] = useState(false);

  const handleVisibleStep = (stepArr: Step[]) => {
    return stepArr.some((stepProp) => stepProp === step);
  };

  const handleNextStep = () => {
    if (handleVisibleStep([Step.upload]) && !validateUpload(certificateInputs.fileName)) {
      return;
    }

    if (handleVisibleStep([Step.info]) && !validateInfo(certificateInputs.description, certificateInputs.hosts)) {
      return;
    }

    const stepperComponent = document.querySelector('bds-stepper');
    if (!stepperComponent) return;

    stepperComponent.setCompletedStep(step);
    stepperComponent.setActiveStep(step + 1);
    setStep(step + 1);
  };

  const handlePreviousStep = () => {
    const stepperComponent = document.querySelector('bds-stepper');
    if (!stepperComponent) return;

    if (isLoading) return;

    stepperComponent.setActiveStep(step - 1);
    setStep(step - 1);
  };

  const getStepContentByIndex = (index) => {
    const content = {
      0: <UploadCertificate />,
      1: <InfoCertificate />,
      2: <CertificateConfirmation />,
    };
    return content[index];
  };

  const handleSetMtlsCertificate = async () => {
    try {
      setIsLoading(true);
      if (!validateFile(certificateInputs.file, content)) {
        return false;
      }

      await mtlsCertificateApi.setMtlsCertificate(certificateInputs, tenantId);

      if (mtlsCertificateApi.getStatus() == RequestStatus.FAILURE) {
        showToast({
          type: 'danger',
          message: content.upload.error,
        });
        return false;
      }

      showToast({
        type: 'success',
        message: content.upload.success,
      });
      setCertificateUpdatedProps({ id: '', certificateUpdated: true });
      return true;
    } catch (e) {
      showToast({
        type: 'danger',
        message: content.upload.error,
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const setActiveStep = (step: Step) => {
    const stepperComponent = document.querySelector('bds-stepper');
    if (!stepperComponent) return;
    stepperComponent.setActiveStep(step);
    setStep(step);
  };

  const handleSubmitCertificate = async () => {
    if (!validateConfirmation(certificateInputs.fileName, certificateInputs.description, certificateInputs.hosts)) {
      return;
    }

    const isCertificateSaved = await handleSetMtlsCertificate();

    if (!isCertificateSaved) {
      return;
    }

    setCertificateInputs(emptyCertificate);
    setActiveStep(Step.upload);
    toggleSubmitModal();
  };

  return (
    <>
      <BdsStepper>
        <BdsStep active={true}>{content.upload.title}</BdsStep>
        <BdsStep>{content.info.title}</BdsStep>
        <BdsStep>{content.confirmation.title}</BdsStep>
      </BdsStepper>
      <div
        style={{
          maxWidth: '40rem',
          width: '40rem',
          maxHeight: '20rem',
          height: '18rem',
          overflowY: 'scroll',
          padding: '2rem',
          margin: '2rem 0',
          border: '1px solid #ccc',
          borderRadius: '5px',
        }}
      >
        {getStepContentByIndex(step)}
      </div>
      <div
        style={{
          display: 'flex',
        }}
      >
        {handleVisibleStep([Step.info, Step.confirmation]) && (
          <BdsButton
            disabled={isLoading}
            style={{
              marginRight: '8px',
            }}
            onClick={handlePreviousStep}
          >
            {content.step.back}
          </BdsButton>
        )}
        {handleVisibleStep([Step.upload]) && (
          <BdsButton disabled={!validateUpload(certificateInputs.fileName)} onClick={handleNextStep}>
            {content.step.next}
          </BdsButton>
        )}
        {handleVisibleStep([Step.info]) && (
          <BdsButton
            disabled={!validateInfo(certificateInputs.description, certificateInputs.hosts)}
            onClick={handleNextStep}
          >
            {content.step.next}
          </BdsButton>
        )}
        {handleVisibleStep([Step.confirmation]) && (
          <BdsButton
            bdsLoading={isLoading}
            disabled={
              !validateConfirmation(certificateInputs.fileName, certificateInputs.description, certificateInputs.hosts)
            }
            onClick={handleSubmitCertificate}
          >
            {content.step.finish}
          </BdsButton>
        )}
      </div>
    </>
  );
};

export default SubmitCertificate;
